import type { ReactNode } from "react";

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  description: string;
  image: string;
  imagePosition?: string;
  actions?: ReactNode;
  stats?: Array<{
    value: string;
    label: string;
  }>;
  children?: ReactNode;
}

export function PageHero({
  eyebrow,
  title,
  description,
  image,
  imagePosition = "center",
  actions,
  stats,
  children,
}: PageHeroProps) {
  return (
    <section className="relative isolate overflow-hidden bg-ink">
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: imagePosition }}
        loading="eager"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,15,27,0.96)_0%,rgba(9,15,27,0.9)_28%,rgba(9,15,27,0.56)_58%,rgba(9,15,27,0.2)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(217,184,118,0.12)_0%,rgba(9,15,27,0)_42%,rgba(9,15,27,0.35)_100%)]" />

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 border-l-2 border-primary-glow pl-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary-glow">
            {eyebrow}
          </span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-tight text-ink-foreground sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-ink-muted sm:text-lg">
            {description}
          </p>
          {actions && <div className="mt-8 flex flex-wrap gap-3">{actions}</div>}
        </div>

        {stats && stats.length > 0 && (
          <dl className="mt-14 grid max-w-4xl grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 backdrop-blur md:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-ink/55 p-5">
                <dt className="text-xs font-medium uppercase tracking-[0.18em] text-ink-muted">
                  {stat.label}
                </dt>
                <dd className="mt-2 font-display text-3xl font-semibold text-primary-glow">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {children}
      </div>
    </section>
  );
}
